import { useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Select, Stack, TextInput } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import { PROCEDURES_ERROR_CODES, usePublicProceduresService } from '@actschurch/ccms-sdk';
import { ProcedureAccessError, SignInRequired } from '@/components/procedure/AccessScreens';
import { ReCaptcha } from '@/components/procedure/ReCaptcha';
import { PlanningCentreButton } from '@/components/auth/PlanningCentreButton';
import { BackLink, Callout, LoadingState, PageIntro, SectionCard, StickyActionBar } from '@/components/ui';
import { ENV } from '@/config/environment';
import { getDeviceId, getRunnerProfile, saveRunnerProfile } from '@/lib/device';
import { errorMessage, isNetworkError, procedureErrorCode } from '@/lib/errors';
import { getMinistriesSnapshot, saveMinistriesSnapshot } from '@/lib/offlineStore';
import { usePortalMode, useProcedureView } from '@/lib/portalApi';
import { proceduresKeys } from '@/lib/queryKeys';
import { setRunAside, useRunStarter } from '@/lib/runLifecycle';
import { getInProgress } from '@/lib/runStore';
import { usePortalSettings } from '@/lib/settings';
import { useSyncStore } from '@/lib/syncQueue';

const OTHER = 'other';

/**
 * /p/:code/start (public procedures, nobody signed in): name and ministry once, remembered on this
 * phone and prefilled next time. Signed-in people never see this screen (their profile is used).
 * Anti-spam: the hidden `website` honeypot always, reCAPTCHA only when the portal settings ask for it.
 */
export default function StartPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { mode, isInitializing } = usePortalMode();
  const { settings } = usePortalSettings();
  const publicProcedures = usePublicProceduresService();
  const query = useProcedureView(code);
  const { startPublic } = useRunStarter(code);
  const online = useSyncStore((s) => s.online);

  const profile = useMemo(() => getRunnerProfile(), []);
  const [name, setName] = useState(profile?.name ?? '');
  const [ministry, setMinistry] = useState<string | null>(
    profile?.ministryID ? String(profile.ministryID) : profile?.ministryOther ? OTHER : null,
  );
  const [ministryOther, setMinistryOther] = useState(profile?.ministryOther ?? '');
  const [website, setWebsite] = useState('');
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [errors, setErrors] = useState<{ name?: string; ministry?: string; other?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [signInNeeded, setSignInNeeded] = useState(false);

  const ministries = useQuery({
    queryKey: proceduresKeys.ministries(),
    queryFn: async () => {
      const list = await publicProcedures.getMinistries();
      saveMinistriesSnapshot(list);
      return list;
    },
    staleTime: 60 * 60 * 1000,
    placeholderData: () => getMinistriesSnapshot() ?? undefined,
  });

  const ministryOptions = useMemo(() => {
    const list = [...(ministries.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    return [...list.map((m) => ({ value: String(m.ministryID), label: m.name })), { value: OTHER, label: 'Other (type it in)' }];
  }, [ministries.data]);

  if (isInitializing) return <div className="proc-main"><LoadingState variant="spinner" label="Loading" /></div>;
  // Signed in: the overview starts the run straight away with the person's profile.
  if (mode === 'member') return <Navigate to={`/p/${encodeURIComponent(code)}`} replace />;
  if (signInNeeded) return <SignInRequired code={code} />;

  const view = query.data?.view;
  if (!view && !query.isError) return <div className="proc-main"><LoadingState label="Opening the procedure" count={1} /></div>;
  if (!view) return <ProcedureAccessError code={code} error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />;

  const existing = getInProgress(code, null);
  const siteKey = settings.recaptchaSiteKey || ENV.RECAPTCHA_SITE_KEY;
  // TODO: when requireRecaptchaForPublicRuns is on but no site key is configured anywhere, the widget is
  // skipped and the API decides (it answers recaptcha_failed if it insists). Configure the key in Settings.
  const needsCaptcha = settings.requireRecaptchaForPublicRuns && !!siteKey;
  const requireMinistry = view.requireMinistry;

  if (!settings.publicRunsEnabled) {
    return (
      <div className="proc-main">
        <BackLink to={`/p/${encodeURIComponent(code)}`} label="Back" />
        <PageIntro eyebrow={view.title} title="Please sign in to run this" lede="Running procedures without signing in is switched off at the moment." />
        <div className="proc-btnstack">
          <PlanningCentreButton redirectTo={`/p/${encodeURIComponent(code)}`} primary />
          <Button component={Link} to={`/auth/login?redirect=${encodeURIComponent(`/p/${code}`)}`} variant="subtle" fullWidth>Sign in with email</Button>
        </div>
      </div>
    );
  }

  const validate = () => {
    const next: typeof errors = {};
    if (name.trim().length < 2) next.name = 'Please type your name.';
    if (requireMinistry && !ministry) next.ministry = 'Please choose the ministry you are serving in.';
    if (ministry === OTHER && ministryOther.trim().length < 2) next.other = 'Please type the name of your ministry.';
    if (needsCaptcha && !captcha) next.form = 'Please tick "I\'m not a robot" first.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (submitting || !validate()) return;
    if (!online) {
      setErrors({ form: 'You need signal to start. Move to where you have signal and tap Start again. After that you can carry on without it.' });
      return;
    }
    const ministryID = ministry && ministry !== OTHER ? Number(ministry) : null;
    const other = ministry === OTHER ? ministryOther.trim() : null;
    saveRunnerProfile({ name: name.trim(), ministryID, ministryOther: other });
    setSubmitting(true);
    try {
      const runId = await startPublic(view.title, {
        runnerName: name.trim(),
        ministryID,
        ministryOther: other,
        deviceId: getDeviceId(),
        recaptchaToken: needsCaptcha ? captcha : null,
        website: website || null,
      });
      // The earlier unfinished run on this phone is set aside only once the new one exists.
      if (existing && existing.runId !== runId) setRunAside(existing);
      navigate(`/p/${encodeURIComponent(code)}/run/${runId}`, { replace: true });
    } catch (err) {
      const c = procedureErrorCode(err);
      if (c === PROCEDURES_ERROR_CODES.SignInRequired) {
        setSignInNeeded(true);
        return;
      }
      if (c === PROCEDURES_ERROR_CODES.RecaptchaFailed) {
        setCaptcha(null);
        setCaptchaKey((k) => k + 1);
      }
      setErrors({
        form: isNetworkError(err)
          ? 'You need signal to start. Move to where you have signal and tap Start again. After that you can carry on without it.'
          : errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="proc-main proc-hasbar">
      <div className="proc-pagebody">
      <BackLink to={`/p/${encodeURIComponent(code)}`} label="Back" />
      <PageIntro
        eyebrow={view.title}
        title={profile ? 'Is this still you?' : "Who's running this?"}
        lede="So the team knows who did it. We'll remember it on this phone for next time."
      />

      {existing && (
        <Callout
          tone="info"
          title="You already started this on this phone"
          action={<Button component={Link} to={`/p/${encodeURIComponent(code)}/run/${existing.runId}`} variant="default">Carry on instead</Button>}
        >
          Starting again sets the earlier run aside.
        </Callout>
      )}

        <SectionCard>
          <Stack gap="md">
            <TextInput
              label="Your name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              error={errors.name}
              required
              autoComplete="name"
              autoCapitalize="words"
              maxLength={120}
              size="lg"
            />
            <Select
              label="Which ministry are you serving in?"
              description={requireMinistry ? undefined : 'Optional'}
              placeholder={ministries.isLoading ? 'Loading ministries...' : 'Choose your ministry'}
              data={ministryOptions}
              value={ministry}
              onChange={(v) => setMinistry(v)}
              error={errors.ministry}
              required={requireMinistry}
              searchable
              clearable={!requireMinistry}
              nothingFoundMessage="Not in the list? Choose Other"
              size="lg"
              comboboxProps={{ withinPortal: true }}
            />
            {ministry === OTHER && (
              <TextInput
                label="Your ministry"
                value={ministryOther}
                onChange={(e) => setMinistryOther(e.currentTarget.value)}
                error={errors.other}
                required
                maxLength={120}
                size="lg"
              />
            )}
            {/* Honeypot: people never see or fill this; bots that do are turned away by the API. */}
            <div className="proc-hp" aria-hidden="true">
              <label htmlFor="proc-website">Website</label>
              <input id="proc-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.currentTarget.value)} />
            </div>
            {needsCaptcha && <ReCaptcha key={captchaKey} siteKey={siteKey} onVerify={setCaptcha} />}
            {errors.form && <Callout tone="bad" live>{errors.form}</Callout>}
          </Stack>
        </SectionCard>
      </div>
        <StickyActionBar>
          <Button type="submit" size="lg" fullWidth loading={submitting} leftSection={<IconPlayerPlay size={22} aria-hidden="true" />}>
            Start
          </Button>
        </StickyActionBar>
    </form>
  );
}
